import { Stack, useRouter } from 'expo-router';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Platform } from 'react-native';
import { useMutation, useQuery } from 'urql';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ModalKeyboardAwareScrollView from '@/components/Custom/ModalKeyboardAwareScrollView';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import Box from '@/components/UI/Box';
import ChipPreferenceRow from '@/components/UI/ChipPreferenceRow';
import ContentBoundary from '@/components/UI/ContentBoundary';
import { NOTIFICATION_PREFERENCE_OPTIONS } from '@/components/UI/NotificationPreference.options';
import PressableRow from '@/components/UI/PressableRow';
import Row from '@/components/UI/Row';
import Select from '@/components/UI/Select';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';
import { graphql } from '@/gql/graphql';
import useNotificationPermissionEnabled from '@/hooks/useNotificationPermissionEnabled';
import openNotificationSettings from '@/utils/openNotificationSettings';

const eventPushIntervalOptions = [
  {
    label: '1 Hour Before',
    value: '60',
  },
  {
    label: '3 Hours Before',
    value: '180',
  },
  {
    label: '6 Hours Before',
    value: '360',
  },
  {
    label: '12 Hours Before',
    value: '720',
  },
  {
    label: '24 Hours Before',
    value: '1440',
  },
];
const GetProfileNotification = graphql(/* GraphQL */ `
  query GetProfileNotification {
    current_user {
      id
      event_push_interval
      chat_notification_preference
    }
  }
`);
const UpdateProfileNotification = graphql(`
  mutation UpdateProfileNotification(
    $id: String!
    $eventPushInterval: Int
    $chatNotificationPreference: chat_notification_preference_enum!
  ) {
    update_person_by_pk(
      pk_columns: { id: $id }
      _set: {
        event_push_interval: $eventPushInterval
        chat_notification_preference: $chatNotificationPreference
      }
    ) {
      id
      event_push_interval
      chat_notification_preference
    }
  }
`);
type ProfileNotificationFormValues = {
  eventPushInterval?: number | null;
  chatNotificationPreference: 'all' | 'mentions' | 'none';
};

export default function ProfileNotification() {
  const [{ data, fetching, error }, refresh] = useQuery({
    query: GetProfileNotification,
    requestPolicy: 'network-only',
  });
  const settings = data?.current_user;

  return (
    <>
      <Stack.Screen options={{ title: 'Notifications' }} />
      <ContentBoundary
        data={settings}
        loading={fetching}
        error={error}
        onRetry={() => refresh({ requestPolicy: 'network-only' })}
        errorMessage="Could not load notification settings."
        empty={<Text>Notification settings are unavailable.</Text>}
      >
        {(settings) => (
          <ProfileNotificationForm
            key={settings.id}
            id={settings.id}
            defaultValues={{
              eventPushInterval: settings.event_push_interval,
              chatNotificationPreference:
                settings.chat_notification_preference ?? 'all',
            }}
          />
        )}
      </ContentBoundary>
    </>
  );
}

function ProfileNotificationForm({
  id,
  defaultValues,
}: {
  id: string;
  defaultValues: ProfileNotificationFormValues;
}) {
  const { back } = useRouter();
  const notificationPermissionEnabled = useNotificationPermissionEnabled();
  const [, updateProfileNotification] = useMutation(UpdateProfileNotification);
  const form = useForm<ProfileNotificationFormValues>({
    defaultValues,
  });
  const {
    control,
    setValue,
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = form;
  const submitForm = async (values: ProfileNotificationFormValues) => {
    const { data, error } = await updateProfileNotification({
      id,
      eventPushInterval: values.eventPushInterval,
      chatNotificationPreference: values.chatNotificationPreference,
    });
    if (!error && data?.update_person_by_pk?.id === id) {
      back();
    }
  };
  const chatNotificationPreference = useWatch({
    control,
    name: 'chatNotificationPreference',
  });
  const eventPushInterval = useWatch({
    control,
    name: 'eventPushInterval',
  });
  const onValueChange = (value: string) => {
    const selectedOption = eventPushIntervalOptions.find(
      (option) => option.value === value,
    );

    if (!selectedOption) {
      return;
    }

    setValue('eventPushInterval', Number(selectedOption.value), {
      shouldDirty: true,
    });
  };
  return (
    <FormProvider {...form}>
      <Stack.Screen
        options={{
          title: 'Notifications',
        }}
      />
      <ModalKeyboardAwareScrollView>
        <ChipPreferenceRow
          options={NOTIFICATION_PREFERENCE_OPTIONS}
          label="Default Chat Notification"
          onValueChange={(value) => {
            setValue('chatNotificationPreference', value, {
              shouldDirty: true,
            });
          }}
          testIDPrefix="ProfileNotificationPreference"
          value={chatNotificationPreference}
        />
        <Row spaced>
          <Text flexShrink={1}>Event Reminders</Text>
          <Box flex={1} maxWidth={220}>
            <Select
              accessibilityLabel="Event Reminders"
              onValueChange={onValueChange}
              options={eventPushIntervalOptions}
              placeholder="Select…"
              testID="ProfileEventPushIntervalSelect"
              value={eventPushInterval?.toString() ?? null}
              width="100%"
            />
          </Box>
        </Row>
        <Box direction="vertical" px="xl" pb="md">
          <Text variant="typographySecondary" size="sm">
            Choose when to get a reminder for events you&apos;ve RSVP&apos;d Yes
            or Maybe to.
          </Text>
        </Box>
        {Platform.OS !== 'web' ? (
          <Box direction="vertical" pb="md" space="sm">
            <PressableRow
              accessibilityLabel="Phone Notification Settings"
              onPress={() => {
                void openNotificationSettings();
              }}
              spaced
              testID="PhoneNotificationSettings"
            >
              <Box direction="vertical" flex space="xs">
                <Text>Phone Notification Settings</Text>
                <Text variant="typographySecondary" size="sm">
                  Manage this app&apos;s notification permissions for this
                  device.
                  {notificationPermissionEnabled === false ? (
                    <Text variant="warning" size="sm">
                      {' '}
                      Notifications are off on this device in phone settings.
                    </Text>
                  ) : null}
                </Text>
              </Box>
              <Icon
                name="chevron-forward"
                size="sm"
                variant="typographySecondary"
              />
            </PressableRow>
          </Box>
        ) : null}
      </ModalKeyboardAwareScrollView>
      <ModalKeyboardStickyView>
        <FormSaveButton
          info={{ text: 'Save' }}
          isDisabled={!isDirty || isSubmitting}
          onPress={handleSubmit(submitForm)}
        />
      </ModalKeyboardStickyView>
    </FormProvider>
  );
}
